import React, { useState, useEffect } from "react";
import { getMyFavorites } from "../api/vibe";
import axios from "axios";
import { apiConfig } from "../config/api";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageHeader from "../components/PageHeader";
import PageContainer from "../components/pages/PageContainer";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import CustomPageHeader from "../components/pages/CustomPageHeader";
import secureStorage from "../utils/secureStorage";

const Favorites = ({ goBack }) => {
  const { t } = useTranslation();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({}); // { [favId]: true/false }
  const navigate = useNavigate();
  // Show vibe details: navigate to VibeDetail page using id in URL
  const handleShowDetails = (vibe) => {
    const id = vibe.id || vibe.vibe_id;
    if (id) {
      navigate(`/vibe/${id}`, { state: { backTo: "/favorite" } });
    }
  };

  // Toggle favorite status
  const handleToggleFavorite = async (fav) => {
    const vibeId = fav.vibe?.id || fav.vibe_id || fav.id;
    if (!vibeId) return;
    setToggling((prev) => ({ ...prev, [fav.id]: true }));
    try {
      const token = secureStorage.getItem("accessToken");
      await axios.post(
        `${apiConfig.basePath}create-remove-favorite`,
        { vibe_id: vibeId },
        {
          headers: {
            Authorization: `Bearer ${token}`,

          },
        }
      );
      // Refresh favorites after toggle
      const res = await getMyFavorites();
      setFavorites(
        Array.isArray(res.data?.data?.data)
          ? res.data.data.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : []
      );
    } catch (err) {
      toast.error("Failed to toggle favorite");
    } finally {
      setToggling((prev) => ({ ...prev, [fav.id]: false }));
    }
  };

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        setLoading(true);
        const res = await getMyFavorites();
        // API returns { data: [...] }
        // API returns { data: { data: [...] } }
        setFavorites(
          Array.isArray(res.data?.data?.data)
            ? res.data.data.data
            : Array.isArray(res.data?.data)
            ? res.data.data
            : []
        );
      } catch (err) {
        toast.error("Failed to load favorites");
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, []);

  return (
    <CustomPageHeader
      pageContainerClass=""
      pageContainerInnerClass="p-2 sm:p-0 md:p-0 h-[calc(100vh-80px)]"
      title={t("favorites", "Vibelinks")}
      onBack={() => navigate(-1)}
      // hideBack={true}
      // showPageHeader={false}
    >
      {/* Content */}
      <div className="mt-4">
        {loading && <Loader />}

        {!loading && favorites.length === 0 && (
          <p className="text-gray-500 dark:text-dark-text-secondary">{t("no_data_found")}</p>
        )}

        {!loading && favorites.length > 0 && (
          <div className="space-y-4">
            {favorites.map((fav) => {
              const vibe = fav.vibe || fav; // fallback to top-level if not nested
              const vibeCategory = vibe.vibe_category?.name || "-";
              const venue = vibe.venue || "-";
              let dateObj = vibe.date ? dayjs(vibe.date) : null;
              const month = dateObj ? dateObj.format("MMM") : "-";
              const day = dateObj ? dateObj.format("DD") : "-";
              const isFavorite =
                vibe.is_favorite === true || fav.is_favorite === true;
              return (
                <div
                  key={fav.id}
                  className="bg-white dark:bg-[#252739] p-3 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-[#2d2f45]"
                  onClick={() => handleShowDetails(vibe)}
                >
                  {/* Date Box */}
                  <div
                    className="flex flex-col items-center rounded-lg justify-center mr-4 bg-[#6d29f6] text-white"
                    style={{ minWidth: 48 }}
                  >
                    <div
                      className="rounded-lg px-2 py-1 text-xs font-bold text-white"
                      style={{ background: "#6d2af6" }}
                    >
                      {month}
                    </div>
                    <div className="text-lg font-bold">{day}</div>
                  </div>
                  {/* Main Info */}
                  <div className="flex-1">
                    <div className="font-semibold text-base text-gray-900 dark:text-white">
                      {vibeCategory}
                    </div>
                    <div className="text-gray-500 dark:text-dark-text-secondary text-sm">{venue}</div>
                  </div>
                  {/* Heart Icon */}
                  <div
                    className="ml-4 z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(fav);
                    }}
                  >
                    {toggling[fav.id] ? (
                      <svg
                        className="animate-spin"
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="#6d2af6"
                          strokeWidth="4"
                          strokeDasharray="60"
                        />
                      </svg>
                    ) : isFavorite ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="#6d2af6"
                        viewBox="0 0 24 24"
                        width="28"
                        height="28"
                      >
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        stroke="#6d2af6"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        width="28"
                        height="28"
                      >
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </CustomPageHeader>
  );
};

export default Favorites;
